import React from "react";
import { AnimatePresence, motion } from "framer-motion";

interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    showCloseButton?: boolean;
    theme?: "light" | "dark";
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    children,
    title,
    showCloseButton = true,
    theme = "light"
}) => {
    // Theme-based styling
    const headerStyles = {
        light: {
            bg: "bg-white",
            border: "border-gray-200",
            titleText: "text-gray-900",
            closeButton: "text-gray-400 hover:text-gray-600"
        },
        dark: {
            bg: "bg-gray-800",
            border: "border-gray-700",
            titleText: "text-white",
            closeButton: "text-gray-500 hover:text-gray-300"
        }
    };

    const currentTheme = headerStyles[theme];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 md:hidden"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="relative w-full overflow-hidden rounded-t-lg shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Optional header with title and close button */}
                        {(title || showCloseButton) && (
                            <div
                                className={`flex items-center justify-between px-4 py-3 ${currentTheme.bg}`}
                            >
                                {title && (
                                    <h3
                                        className={`text-sm font-semibold ${currentTheme.titleText}`}
                                    >
                                        {title}
                                    </h3>
                                )}
                                {!title && <div />} {/* Spacer when no title */}
                                {showCloseButton && (
                                    <button
                                        onClick={onClose}
                                        className={`${currentTheme.closeButton} transition-colors`}
                                        aria-label="Close"
                                    >
                                        <i className="far fa-times" />
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Content area - full control to children */}
                        {children}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
